<template>
    <div class="container">
        <CommonBreadcrumb class="bread_block" /> 
        <div class="banner text-center">
            <picture>
                <source media="(min-width: 768px)" width="1220" height="424" type="image/webp"
                    srcset="~/assets/four_cs/banner.webp">
                <source media="(min-width: 768px)" width="1220" height="424" type="image/jpeg"
                    srcset="~/assets/four_cs/banner.jpg">

                <source media="(max-width: 767px)" width="319" height="424" type="image/webp"
                    srcset="~/assets/four_cs/banner_mob.webp">
                <source media="(max-width: 767px)" width="319" height="424" type="image/jpeg"
                    srcset="~/assets/four_cs/banner_mob.jpg">
                <img class="radious_eight" width="1220" height="424" type="image/jpeg" src="~/assets/four_cs/banner.jpg"
                    alt="4 Cs of Diamonds">
            </picture>
        </div>

        <div class="four_block">
            <div class="my-5 py-3 text-center">
                <h1 class="mb-4 font-active" v-if="$country.is_india">The 4 C's of Diamonds</h1>
                <h1 class="mb-4 font-active" v-else>The 4 Cs of Diamonds</h1>
                <p v-if="$country.is_india">The Gemological Institute of America (<NuxtLink
                        :to="localePath({ name: 'loose-listing', params: { start_with: 'gia-certified' } })">GIA
                    </NuxtLink>) established the 4 C's as the fundamental criteria for assessing diamond quality on a
                    global scale. These factors, including Cut, Colour, Clarity and Carat, play a pivotal role in
                    determining both the visual appeal and value of a diamond.</p>
                <p v-else>The 4 Cs of diamonds are a <NuxtLink :to="localePath('/blog/what-is-diamond-grading')">diamond
                        grading</NuxtLink> system created by GIA to guide you in assessing the quality of a diamond
                    before buying it. The 4Cs of diamonds grading report include the diamond's color, clarity, cut, and
                    carat weight. The 4 Cs of diamond certificate is the most reliable way to verify and authenticate
                    your gemstone when buying a diamond for <NuxtLink :to="localePath('/engagement-rings')">engagement
                        rings</NuxtLink> and other {{ $t('jewelry') }}.</p>
            </div>

            <div class="row four_type">
                <div class="col-md-6 col-12" id="cut">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp" srcset="~/assets/four_cs/cut.webp">
                            <img class="radious_eight" width="350" height="350" src="~/assets/four_cs/cut.jpg" alt="cut">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active">Cut</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        Contrary to a common misconception, a diamond's cut isn't solely about its shape; it involves
                        how its facets interact with light. This interaction allows light to pass through, creating the
                        sparkle that defines a diamond. A skilled craftsman meticulously cuts a diamond to ensure proper
                        proportions and symmetry, with excellent and very good cuts delivering exceptional brilliance. A
                        good cut strikes a balance between quality and value, while a fair cut may exhibit light leakage
                        and a poor cut leads to significant dullness.
                    </p>
                    <p class="mb-0" v-else>
                        Many believe that a diamond cut grade refers to a diamond shape. This is not true. A
                        <NuxtLink :to="localePath('/engagement-ring-guide/types-of-diamonds')">diamond cut</NuxtLink>
                        refers to the way a diamond's facets are cut for it to interact with light. Diamonds can sparkle
                        intensely and transmit light. How the diamond is cut will affect the stone's brilliance, fire,
                        and scintillation. An expert artisan must cut a diamond to its exact proportions and symmetry to
                        have an excellent light return before polishing the stone.
                    </p>
                </div>
                <div class="col-md-6 col-12" id="carat">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp" srcset="~/assets/four_cs/carat.webp">
                            <img width="350" height="350" src="~/assets/four_cs/carat.jpg" alt="carat">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active">Carat</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        Diamond weight is measured in carats, where one carat equals 200 milligrams. This standardized
                        unit facilitates the accurate measurement of a diamond, not just indicating how big a diamond
                        appears but actually reflecting its total weight. While larger diamonds usually have higher
                        carat weights and are often considered more valuable because of their size, it's important to
                        understand that a diamond's quality and beauty aren't solely defined by carat. Other factors
                        also play a role in its overall visual appeal.
                    </p>
                    <p class="mb-0" v-else>
                        A diamond carat weight refers to the weight of a diamond. A diamond is weighed by metric carats.
                        One metric carat equals 200 milligrams. A diamond carat is further subdivided into 100 points
                        called cents to obtain a precise measurement up to the hundredth decimal place. Since bigger
                        diamonds are rarer and more desirable, diamonds with a higher carat weight are costlier.
                    </p>
                </div>
            </div>
            <div class="row four_type">
                <div class="col-md-6 col-12 mt-4" id="color">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp" srcset="~/assets/four_cs/color.webp">
                            <img width="350" height="350" src="~/assets/four_cs/color.jpg" alt="color">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active" v-if="$country.is_india">Colour</h3>
                    <h3 class="my-4 font-active" v-else>Color</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        A diamond's colour is influenced by trace impurities in its crystal lattice, impacting its
                        appearance and value. Lab diamonds come in various colours, such as colourless, near colourless,
                        faint yellow, light yellow and brown. The Gemological Institute of America (GIA) grades diamond
                        colour on a scale from D (colourless) to Z (light yellow or brown). However, D is a higher
                        colour grade, which indicates less colour and greater value. Whereas Z is the lowest colour
                        grade, which indicates more colour and a lesser value.
                    </p>
                    <p class="mb-0" v-else>
                        The color of a diamond is assessed on the absence of color. High-quality diamonds have no color
                        hue making them colorless diamonds of more value. Diamonds are color graded from D to Z. This
                        color grading system evaluates the extent of the diamond's colorlessness. Gemologists place the
                        diamonds in controlled lighting to determine their color grades. The color differences can
                        sometimes be subtle, but these differences affect the diamond's price and quality.
                    </p>
                </div>
                <div class="col-md-6 col-12 mt-4" id="clarity">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp" srcset="~/assets/four_cs/clarity.webp">
                            <img width="350" height="350" src="~/assets/four_cs/clarity.jpg" alt="clarity">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active">Clarity</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        Clarity measures the purity of a diamond by assessing inclusions (internal imperfections) and
                        blemishes (external imperfections). The clarity scale ranges from Fl (flawless) to I3
                        (included). A flawless diamond (FI) exhibits no inclusions or blemishes visible to the naked
                        eye, even under 10x magnification. In contrast, an I3 diamond has numerous visible inclusions
                        and blemishes.
                    </p>
                    <p class="mb-0" v-else>
                        The diamond creation process can lead to various internal attributes called inclusions, and
                        external attributes called blemishes. The absence of these blemishes and inclusions denotes the
                        diamond's clarity grade. Gemologists must evaluate the clarity of the diamond by assessing its
                        blemishes and inclusions to know how they shall affect the diamond's overall appearance. A
                        diamond's clarity grade has six categories that are further divided and range from Included (I1,
                        I2, and I3) to Flawless (FL).
                    </p>
                </div>
            </div>

            <div class="mt-5 pt-4 text-center">
                <h2 class="mb-4 font-active" v-if="$country.is_india">The Other 2 C’s</h2>
                <h2 class="mb-4 font-active" v-else>The Other 2 Cs</h2>
                <p v-if="$country.is_india">
                    Friendly Diamonds introduces two more C's – Certification and Conscience – shedding light on the
                    authenticity and social impact of each diamond. These aspects play an important role in guiding
                    buyers towards making well-informed and socially responsible purchases.
                </p>
                <p v-else>
                    While the 4 Cs of diamonds are important when grading a diamond, the Cs of certificate and
                    conscience are also equally important.
                </p>
            </div>

            <div class="row four_type">
                <div class="col-md-6 col-12 mt-4">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp"
                                srcset="~/assets/four_cs/certification.webp">
                            <img class="img-fluid radious_eight" width="350" height="350" src="~/assets/four_cs/certification.jpg"
                                alt="Certification">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active">Certification</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        The GIA developed a certification system to evaluate diamond quality based on the 4 C’s—carat,
                        colour, clarity and cut.
                        <NuxtLink :to="localePath('/blog/know-about-diamond-certification')">Diamond certifications
                        </NuxtLink>
                        furnish buyers with detailed information about a diamond's characteristics, aiding informed
                        purchase decisions. Additionally, certifications ensure diamonds are authentic and comply with
                        international standards.
                    </p>
                    <p class="mb-0" v-else>
                        It is imperative to obtain a diamond certificate from your online jeweler when purchasing a
                        diamond. Your diamond certification is a diamond grading report based on the 4Cs of diamonds.
                        Independent diamond grading institutions like
                        <NuxtLink :to="localePath('/blog/igi-certificate-vs-gia-certified-diamonds')">GIA, IGI
                        </NuxtLink>, and GCAL provide the certification to authenticate the quality of your stone.
                    </p>
                </div>
                <div class="col-md-6 col-12 mt-4">
                    <div class="d-flex justify-content-center justify-content-md-start">
                        <picture>
                            <source width="350" height="350" type="image/webp"
                                srcset="~/assets/four_cs/conscience.webp">
                            <img class="img-fluid radious_eight" width="350" height="350" src="~/assets/four_cs/conscience.jpg"
                                alt="Conscience">
                        </picture>
                    </div>
                    <h3 class="my-4 font-active">Conscience</h3>
                    <p class="mb-0" v-if="$country.is_india">
                        <NuxtLink :to="localePath('/lab-diamonds')">Lab diamonds</NuxtLink> offer a compelling solution
                        to the ethical concerns entangled with traditional mining practices in the diamond industry.
                        Opting for lab grown diamonds represents a conscious choice to embrace a gem with a humane
                        origin, untethered from the ethical shadows that have often plagued the conventional diamond
                        trade.
                    </p>
                    <p class="mb-0" v-else>
                        Conscience is about making an ethical and sustainable choice when shopping for diamonds. The
                        diamond mining industry, infamous for its unethical practices, poses a big risk to the
                        environment at large.
                        <NuxtLink :to="localePath('/lab-diamonds')">Lab diamonds</NuxtLink>
                        are part of a responsible diamond supply chain that guarantees a smaller carbon footprint is
                        left on the planet, making it a conscientious buy.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
const localePath = useLocalePath()
const { $country } = useNuxtApp()
useFetchMeta( useNuxtApp(), useRuntimeConfig() );
</script>

<style scoped>
h1,
h2 {
    font-size: 40px;
}

p {
    font-size: 16px;
    color: #6a6161;
    font-weight: 500;
}

p a {
    text-decoration: underline;
}

.four_block {
    width: 75%;
    margin: 0 auto;
}

.four_type h3 {
    font-size: 26px;
}

.four_type p {
    font-size: 16px;
    color: #6a6161;
}

@media(max-width: 992px) {
    .four_block {
        width: 100%;
        margin: 0 auto;
    }
}

@media(max-width: 767px) {
    .banner img {
        width: 100%;
    }

    .four_block {
        width: 100%;
        margin: 0 auto;
    }

    h1,
    h2 {
        font-size: 32px;
    }

    .four_type h3 {
        font-size: 24px;
    }

    .four_type p {
        font-family: 'Lato';
    }
}
</style>